import { PhoneNumberFormat, PhoneNumberUtil } from "google-libphonenumber";
import { isProductionEnvironment } from "@remi/environment";

export const isValidPhoneNumber = (phoneNumber: string): boolean => {
  try {
    const util = PhoneNumberUtil.getInstance();
    const parsed = util.parse(phoneNumber, "US");
    const formatted = util.format(parsed, PhoneNumberFormat.E164);
    if (!isProductionEnvironment() && formatted.startsWith("+1555") && formatted.length === 12) {
      return true;
    }
    return util.isValidNumber(parsed);
  } catch {
    return false;
  }
};

export const formatPhoneNumberForDatabase = (phoneNumber: string): string => {
  //Formats phone numbers to E.164 format assuming US number, otherwise it returns the input string
  const digitsOnly = phoneNumber.replace(/\D/g, "");
  if (digitsOnly.length === 11 && digitsOnly[0] === "1") {
    phoneNumber = digitsOnly.slice(1);
  }

  try {
    const util = PhoneNumberUtil.getInstance();
    const parsed = util.parse(phoneNumber, "US");
    return util.format(parsed, PhoneNumberFormat.E164);
  } catch {
    return phoneNumber;
  }
};

type FormatPhoneNumberForDisplayOptions = {
  extension?: string | null;
  format?: PhoneNumberFormat;
};

export const formatPhoneNumberForDisplay = (
  phoneNumber: string,
  options?: FormatPhoneNumberForDisplayOptions,
): string => {
  try {
    const util = PhoneNumberUtil.getInstance();
    const parsed = util.parse(formatPhoneNumberForDatabase(phoneNumber), "E.164");
    return (
      util.format(parsed, options?.format ?? PhoneNumberFormat.INTERNATIONAL) +
      (options?.extension ? ` Ext ${options.extension}` : "")
    );
  } catch {
    return phoneNumber;
  }
};

export const formatPhoneNumberForDisplayNational = (phoneNumber: string): string =>
  formatPhoneNumberForDisplay(phoneNumber, { format: PhoneNumberFormat.NATIONAL });

/**
 * Strips all non-numeric characters from a phone string, keeping only digits
 * and a leading '+' for international numbers.
 */
export const stripPhoneToDigits = (phone: string | undefined): string | undefined => {
  if (!phone) {
    return phone;
  }
  const stripped = phone.replace(/[^\d+]/g, "").replace(/(?!^)\+/g, "");
  return stripped.length > 0 ? stripped : undefined;
};

export const formatPhoneNumberDigitsOnly = (phoneNumber: string): string => {
  return phoneNumber.replace(/\D/g, "");
};

export const formatPhoneNumberNoCountryCode = (phoneNumber: string): string => {
  return formatPhoneNumberDigitsOnly(phoneNumber).slice(-10);
};

export const formatPhoneNumberForDisplayRealtime = (rawValue: string): string => {
  if (!rawValue) {
    return "";
  }

  let digitsOnly = rawValue.replace(/\D/g, "");

  // Handle US country code: if 11 digits and starts with 1, remove the leading 1
  if (digitsOnly.length === 11 && digitsOnly[0] === "1") {
    digitsOnly = digitsOnly.slice(1);
  }

  if (digitsOnly.length <= 3) {
    return digitsOnly;
  } else if (digitsOnly.length <= 6) {
    return `(${digitsOnly.slice(0, 3)}) ${digitsOnly.slice(3)}`;
  } else if (digitsOnly.length <= 10) {
    return `(${digitsOnly.slice(0, 3)}) ${digitsOnly.slice(3, 6)}-${digitsOnly.slice(6, 10)}`;
  } else {
    // For more than 10 digits, truncate to first 10
    const truncated = digitsOnly.slice(0, 10);
    return `(${truncated.slice(0, 3)}) ${truncated.slice(3, 6)}-${truncated.slice(6, 10)}`;
  }
};

export const getRawPhoneNumber = (formattedValue: string): string => {
  return formattedValue.replace(/\D/g, "");
};

export { PhoneNumberFormat };
